// src/components/homepage/HomePageOfferings.tsx
import React from 'react';
import { motion } from 'framer-motion';
import { Briefcase, Cog, Layers } from 'lucide-react'; // Icons for offerings
import {ContentCard} from '@dopomogai/ui'; // Assuming path: src/components/ui/ContentCard.tsx

// Interface for a single of  fering block's data (pre-translated)
interface OfferingBlockData {
  title: string;
  text: string;
  cta: string; // CTA button text
}

// Interface for the entire offerings section data (pre-translated)
interface OfferingsData {
  headline: string;
  intro: string;
  block1: OfferingBlockData;
  block2: OfferingBlockData;
  block3: OfferingBlockData;
}

interface HomePageOfferingsProps {
  offeringsData: OfferingsData;
  onAction: (actionType: string, payload?: any) => void;
  id?: string;
}

export const HomePageOfferings: React.FC<HomePageOfferingsProps> = ({ offeringsData, onAction, id }) => {
  // console.log('[HomePageOfferings.tsx] Received offeringsData:', JSON.stringify(offeringsData, null, 2));

  const offeringCardsConfig = [
    {
      iconElement: <Briefcase size={32} strokeWidth={1.5} className="text-emerald-600" />,
      data: offeringsData.block1,
      actionPayload: { path: '/solutions/custom-ai-employees' }, // Example specific path
      accentColor: 'emerald', // For icon bubble and subtle card bg
    },
    {
      iconElement: <Cog size={32} strokeWidth={1.5} className="text-sky-600" />,
      data: offeringsData.block2,
      actionPayload: { path: '/solutions/app-enhancement' }, // Example specific path
      accentColor: 'sky',
    },
    {
      iconElement: <Layers size={32} strokeWidth={1.5} className="text-purple-600" />,
      data: offeringsData.block3,
      actionPayload: { path: '/platform/early-access' }, // Example specific path
      accentColor: 'purple',
    },
  ];

  const headerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
  };

  const gridVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.2, // Delay before first card starts animating
        staggerChildren: 0.15, // Time between each card's animation
      },
    },
  };

  return (
    <section id={id} className="bg-white py-16 md:py-24">
      <div className="container mx-auto px-4">
        <motion.h2
          variants={headerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          className="text-3xl md:text-4xl lg:text-5xl font-bold text-center text-gray-900 mb-5"
        >
          {offeringsData.headline}
        </motion.h2>
        <motion.p
          variants={headerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.5, delay: 0.1, ease: 'easeOut' }} // Slightly delayed after headline
          className="mt-4 max-w-3xl mx-auto text-center text-lg md:text-xl text-slate-600 mb-12 md:mb-16"
        >
          {offeringsData.intro}
        </motion.p>

        <motion.div
          className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-10"
          variants={gridVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }} // Trigger when 10% of the grid is visible
        >
          {offeringCardsConfig.map((cardItem, index) => (
            <ContentCard
              key={cardItem.data.title} // Ensure unique key
              index={index} // Used by ContentCard for its own staggered animation logic
              icon={
                <div className={`p-3 rounded-lg border-2 border-black inline-block shadow-[3px_3px_0_0_black] bg-${cardItem.accentColor}-100`}>
                  {cardItem.iconElement}
                </div>
              }
              title={cardItem.data.title}
              text={cardItem.data.text}
              cta={{
                text: cardItem.data.cta,
                onClick: () => onAction('NAVIGATE_TO_PAGE', cardItem.actionPayload),
                variant: 'secondary', // Consistent CTA style for these cards
                ariaLabel: `Learn more about ${cardItem.data.title}`,
              }}
              className={`bg-white border-black`} // Card itself is white with black border
              contentClassName={`p-6 md:p-8`} // Standard padding for content area
              // Optional: If you want a very subtle background tint in the content area based on accent:
              // contentClassName={`p-6 md:p-8 bg-${cardItem.accentColor}-50/30 rounded-b-lg -m-px mt-px`}
              iconContainerClassName="mb-5" // More space after icon
              titleClassName="!text-xl md:!text-2xl !leading-tight"
              textClassName="!text-slate-600 !text-base"
              hoverEffect="lift" // Neobrutalist lift effect
            />
          ))}
        </motion.div>
      </div>
    </section>
  );
};

